import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, FileText, User, Shield, DollarSign, MapPin, Calendar, LogOut, Menu, X, Briefcase, CheckCircle2, Clock, Award, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TooltipTour, HelpButton, driverTourSteps, driverTutorials } from '../components/TutorialSystem';
import PaymentSetup from '../components/PaymentSetup';

export default function DriverDashboard() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [stats, setStats] = useState({});
  const [profile, setProfile] = useState(null);
  const [driverJobs, setDriverJobs] = useState([]);
  const [tourComplete, setTourComplete] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [showCreateProfileDialog, setShowCreateProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({
    license_type: 'CDL-A',
    license_number: '',
    license_state: '',
    license_expiry: '',
    years_experience: '',
    certifications: '',
    bio: ''
  });
  
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        apiClient.get('/stats/dashboard'),
        apiClient.get('/drivers/jobs'),
      ]);
      setStats(statsRes.data);
      setDriverJobs(jobsRes.data);
      
      // Try to get profile
      try {
        const profileRes = await apiClient.get('/drivers/profile');
        setProfile(profileRes.data);
      } catch {
        setProfile(null);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...profileData,
        license_expiry: new Date(profileData.license_expiry).toISOString(),
        years_experience: parseInt(profileData.years_experience),
        certifications: profileData.certifications.split(',').map(c => c.trim()).filter(Boolean),
      };
      const res = await apiClient.post('/drivers/profile', data);
      setProfile(res.data);
      setShowCreateProfileDialog(false);
      toast.success('Profile created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handleApplyToJob = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/drivers/jobs/${selectedJob.id}/apply`, {
        driver_job_id: selectedJob.id,
        cover_letter: coverLetter
      });
      toast.success('Application submitted!');
      setShowApplyDialog(false);
      setCoverLetter('');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    }
  };

  const handleSubscribe = async () => {
    try {
      const res = await apiClient.post('/subscriptions/create-checkout');
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start payment process');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user?.subscription_status !== 'active') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Complete Your Setup</h2>
            <p className="text-slate-600 mt-2">
              Add a payment method to start applying for jobs. Free to join - you only pay $5 when a job is accepted.
            </p>
          </div>
          <PaymentSetup 
            onSuccess={() => {
              updateUser({ ...user, subscription_status: 'active' });
              navigate('/subscription/success');
            }}
            onCancel={handleLogout}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-orange-500" />
          <span className="font-bold text-slate-900">True Choice Enterprise</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-sidebar-btn">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="p-6">
          <div className="flex items-center gap-2 text-white mb-8">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold">True Choice</span>
          </div>
          
          <nav className="space-y-2">
            <button onClick={() => setActiveTab('profile')} className={`sidebar-link w-full ${activeTab === 'profile' ? 'sidebar-link-active' : ''}`} data-testid="nav-profile">
              <User className="h-5 w-5" /> My Profile
            </button>
            <button onClick={() => setActiveTab('jobs')} className={`sidebar-link w-full ${activeTab === 'jobs' ? 'sidebar-link-active' : ''}`} data-testid="nav-jobs">
              <Briefcase className="h-5 w-5" /> Find Jobs
            </button>
            <button onClick={() => setActiveTab('applications')} className={`sidebar-link w-full ${activeTab === 'applications' ? 'sidebar-link-active' : ''}`} data-testid="nav-applications">
              <FileText className="h-5 w-5" /> Applications
            </button>
          </nav>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-800">
          <div className="text-slate-400 text-sm mb-2">{user?.email}</div>
          <button onClick={handleLogout} className="sidebar-link w-full text-red-400 hover:text-red-300" data-testid="logout-btn">
            <LogOut className="h-5 w-5" /> Log Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.first_name}!</h1>
              <p className="text-slate-600">Driver Dashboard</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="stat-card">
              <div className="stat-value">{stats.applications || 0}</div>
              <div className="stat-label">Total Applications</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.pending_applications || 0}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card flex items-center gap-3">
              {stats.profile_complete ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : (
                <Clock className="h-8 w-8 text-amber-500" />
              )}
              <div>
                <div className="stat-value text-lg">{stats.profile_complete ? 'Complete' : 'Incomplete'}</div>
                <div className="stat-label">Profile Status</div>
              </div>
            </div>
            <div className="stat-card flex items-center gap-3">
              <Briefcase className="h-8 w-8 text-orange-500" />
              <div>
                <div className="stat-value text-lg">{stats.applications || 0}</div>
                <div className="stat-label">Applications Sent</div>
              </div>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">My Driver Profile</h2>
              </div>
              
              {!profile ? (
                <div className="empty-state py-16">
                  <User className="empty-state-icon" />
                  <p className="empty-state-text mb-4">No profile created yet</p>
                  <Button onClick={() => setShowCreateProfileDialog(true)} className="btn-accent" data-testid="create-profile-btn">
                    Create Driver Profile
                  </Button>
                </div>
              ) : (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4">Personal Information</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-slate-500">Name</span>
                          <p className="font-medium">{profile.first_name} {profile.last_name}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Email</span>
                          <p className="font-medium">{profile.email}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Phone</span>
                          <p className="font-medium">{profile.phone}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4">License Information</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-slate-500">License Type</span>
                          <p className={`license-badge ${profile.license_type?.includes('CDL') ? 'license-badge-cdl' : ''} mt-1`}>
                            {profile.license_type}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">License Number</span>
                          <p className="font-medium mono">{profile.license_number}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">State</span>
                          <p className="font-medium">{profile.license_state}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Expiry</span>
                          <p className="font-medium">{format(new Date(profile.license_expiry), 'MMM d, yyyy')}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Years Experience</span>
                          <p className="font-medium">{profile.years_experience} years</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {profile.bio && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-slate-900 mb-2">About</h3>
                      <p className="text-slate-600">{profile.bio}</p>
                    </div>
                  )}
                  
                  {profile.certifications?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-slate-900 mb-2">Certifications</h3>
                      <div className="flex flex-wrap gap-2">
                        {profile.certifications.map((cert, i) => (
                          <span key={i} className="badge bg-blue-100 text-blue-700">
                            <Award className="h-3 w-3 mr-1" /> {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Available Driving Jobs</h2>
              </div>
              {driverJobs.length === 0 ? (
                <div className="empty-state py-16">
                  <Briefcase className="empty-state-icon" />
                  <p className="empty-state-text">No jobs available at the moment</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {driverJobs.map((job) => (
                    <div key={job.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{job.title}</h3>
                          <p className="text-sm text-slate-600 mt-1">{job.company_name || job.carrier_name}</p>
                          <p className="text-sm text-slate-500 mt-2">{job.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-3">
                            <span className={`license-badge ${job.license_required?.includes('CDL') ? 'license-badge-cdl' : ''}`}>
                              {job.license_required}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4" />
                              {job.salary_range}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.location}
                            </span>
                          </div>
                        </div>
                        <Button
                          onClick={() => { setSelectedJob(job); setShowApplyDialog(true); }}
                          className="btn-accent"
                          disabled={!profile}
                          data-testid={`apply-job-${job.id}`}
                        >
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">My Applications</h2>
              </div>
              <div className="empty-state py-16">
                <FileText className="empty-state-icon" />
                <p className="empty-state-text">Your job applications will appear here</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create Profile Dialog */}
      <Dialog open={showCreateProfileDialog} onOpenChange={setShowCreateProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Driver Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProfile} className="space-y-4 mt-4">
            <div>
              <Label>License Type</Label>
              <Select value={profileData.license_type} onValueChange={(v) => setProfileData({ ...profileData, license_type: v })}>
                <SelectTrigger data-testid="license-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDL-A">CDL-A</SelectItem>
                  <SelectItem value="CDL-B">CDL-B</SelectItem>
                  <SelectItem value="CDL-C">CDL-C</SelectItem>
                  <SelectItem value="Non-CDL">Non-CDL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Number</Label>
                <Input
                  value={profileData.license_number}
                  onChange={(e) => setProfileData({ ...profileData, license_number: e.target.value })}
                  required
                  data-testid="license-number-input"
                />
              </div>
              <div>
                <Label>License State</Label>
                <Input
                  value={profileData.license_state}
                  onChange={(e) => setProfileData({ ...profileData, license_state: e.target.value })}
                  maxLength={2}
                  required
                  data-testid="license-state-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Expiry</Label>
                <Input
                  type="date"
                  value={profileData.license_expiry}
                  onChange={(e) => setProfileData({ ...profileData, license_expiry: e.target.value })}
                  required
                  data-testid="license-expiry-input"
                />
              </div>
              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  value={profileData.years_experience}
                  onChange={(e) => setProfileData({ ...profileData, years_experience: e.target.value })}
                  required
                  data-testid="years-exp-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Certifications (comma-separated)</Label>
              <Input
                value={profileData.certifications}
                onChange={(e) => setProfileData({ ...profileData, certifications: e.target.value })}
                placeholder="Hazmat, Tanker, Doubles/Triples"
              />
            </div>
            
            <div>
              <Label>Bio</Label>
              <Textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                placeholder="Tell carriers about yourself..."
                rows={3}
              />
            </div>
            
            <Button type="submit" className="btn-accent w-full" data-testid="submit-profile-btn">
              Create Profile
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for {selectedJob?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApplyToJob} className="space-y-4 mt-4">
            <div>
              <Label>Cover Letter (Optional)</Label>
              <Textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Tell the carrier why you're a great fit..."
                rows={5}
              />
            </div>
            <Button type="submit" className="btn-accent w-full" data-testid="submit-application-btn">
              Submit Application
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tooltip Tour for first-time users */}
      {!loading && (
        <TooltipTour 
          steps={driverTourSteps} 
          storageKey="driver_tour_complete"
          onComplete={() => setTourComplete(true)}
        />
      )}

      {/* Help Button with Video Tutorials */}
      <HelpButton tutorials={driverTutorials} />
    </div>
  );
}
